import { portalText, portalLocale } from "../../i18n/portal.js";
/**
 * My security's words from facts (plan-014 C1): relative times ("Changed 3 months ago", "2 hours ago"), the short
 * date ("added 3 Jun"), the device words and the outcome sentences the prototype's toasts carry. Pure, so a suite
 * pins each sentence.
 */
import type { PersonDeviceView, RemovableSignInMethod, SignInMethodsView } from "../../api/slice/personal-security.js";

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;


/** "now", "5 minutes ago", "2 hours ago", "9 days ago", "3 months ago", "2 years ago". */
export function agoText(iso: string, now: Date): string {
  const elapsed = Math.max(0, now.getTime() - new Date(iso).getTime());
  if (elapsed < MINUTE_MS) return portalText("core", "personal_security.now_c9bc84");
  if (elapsed < HOUR_MS) return portalText("core", "personal_security.ago_minute", { count: Math.floor(elapsed / MINUTE_MS) });
  if (elapsed < DAY_MS) return portalText("core", "personal_security.ago_hour", { count: Math.floor(elapsed / HOUR_MS) });
  if (elapsed < 30 * DAY_MS) return portalText("core", "personal_security.ago_day", { count: Math.floor(elapsed / DAY_MS) });
  if (elapsed < 365 * DAY_MS) return portalText("core", "personal_security.ago_month", { count: Math.floor(elapsed / (30 * DAY_MS)) });
  return portalText("core", "personal_security.ago_year", { count: Math.floor(elapsed / (365 * DAY_MS)) });
}

function SHORT_DATE(): Intl.DateTimeFormat { return new Intl.DateTimeFormat(portalLocale() === "en" ? "en-GB" : "el-GR", { day: "numeric", month: "short" }); }

/** "3 Jun". */
export function shortDateText(iso: string): string {
  return SHORT_DATE().format(new Date(iso));
}

export const METHOD_WORD: Readonly<Record<RemovableSignInMethod, string>> = {
  get password() { return portalText("core", "auth.password_8be3c9"); },
  get google() { return portalText("core", "account_admin.google_2b681c"); },
  get microsoft() { return portalText("core", "auth.microsoft_11f324"); },
};

/** "Google unlinked. You can still sign in with your password." */
export function unlinkedText(method: RemovableSignInMethod, before: SignInMethodsView): string {
  const left: string[] = [];
  if (method !== "password" && before.password.enabled) left.push(portalText("core", "personal_security.your_password_3bed76"));
  if (method !== "google" && before.google.linked) left.push(portalText("core", "account_admin.google_2b681c"));
  if (method !== "microsoft" && before.microsoft.linked) left.push(portalText("core", "auth.microsoft_11f324"));
  const head = method === "password" ? portalText("core", "personal_security.password_removed_1175b2") : portalText("core", "personal_security._unlinked_624f09", { value0: METHOD_WORD[method] });
  return left.length === 0 ? head : portalText("core", "personal_security._you_can_still_sign_in_with_7968ed", { value0: head, value1: left.join(portalText("core", "personal_security._or_439ac6")) });
}

/** "Signed out of 2 other devices." */
export function signedOutText(count: number): string {
  if (count === 0) return portalText("core", "personal_security.you_were_not_signed_in_anywhere_else_df1042");
  return portalText("core", "personal_security.signed_out_devices", { count });
}

/** "Password changed. Other devices were signed out." */
export function passwordChangedText(signedOut: number, wasSet: boolean): string {
  const head = wasSet ? portalText("core", "personal_security.password_changed_47fa52") : portalText("core", "personal_security.password_set_39f78f");
  return signedOut > 0 ? portalText("core", "personal_security._other_devices_were_signed_out_1dfb22", { value0: head }) : head;
}

/**
 * The device column: "Chrome on Mac", "wetalk app · iPhone" / "wetalk app · Android" for the wetalk app (built from
 * `clientKind` and `devicePlatform`, never from a stored label — plan-034 WAPP-DEVICE), or what the list knows.
 */
export function deviceText(device: PersonDeviceView): string {
  if (device.operatorActing) return portalText("core", "personal_security.wetalk_support_acting_for_you_edbbcc");
  if (device.clientKind === "wetalk_app") {
    return device.devicePlatform === "ios"
      ? portalText("core", "personal_security.wetalk_app_iphone")
      : portalText("core", "personal_security.wetalk_app_android");
  }
  return device.userAgentLabel === null ? portalText("core", "personal_security.unknown_browser_58c51e") : device.userAgentLabel;
}

/** The place column: "Rome, IT" when a place is known. */
export function placeText(device: PersonDeviceView): string {
  return device.approximatePlace === null ? portalText("core", "personal_security.not_known_9fedde") : device.approximatePlace;
}

/** "8 of 10 left". */
export function recoveryLeftText(methods: SignInMethodsView): string {
  return portalText("core", "personal_security._of_left_cc7020", { value0: String(methods.recoveryCodeRemaining), value1: String(methods.recoveryCodeTotal) });
}
